import React, { useEffect, useState } from "react";
import styles from "./WastageTable.module.css";
import { IBranch } from "types/Branch";
import moment from "moment";
import { DataTableWithDates } from "components/dataTableWithDates/DataTableWithDates";
import { IWastageForAllBranches, IWastageFood } from "types/WastageFood";
import {
  invalidateWastagesEntriesCache,
  useQueryWastages,
} from "hooks/WastageFoods";
import { useQuerySelectedFromToDates } from "hooks/SelectedFromToDates";
import Toast, { IToastBasicProps } from "components/Toast/Toast";
import { FMSError } from "types/Error";
import { Button } from "@mui/material";
import { displayNameForUnitOfMeasure } from "types/MeasurementUnit";
import CSVDownload from "components/utils/CSVDownload";
import { getWastageDataForExport } from "lib/CSVExportUtils";
import { ModifyWastageModal } from "components/modifyWastageModal/ModifyWastageModal";
import { Auth } from "lib/Auth";
import { useHistory, useLocation } from "react-router-dom";

export const wastageForBranch = (
  wastageWithBranches: IWastageForAllBranches[],
  selectedBranch: IBranch
): IWastageFood[] => {
  for (let i = 0; i < wastageWithBranches.length; i++) {
    if (wastageWithBranches[i].branchId === selectedBranch.id) {
      return wastageWithBranches[i].wastageFoodList;
    }
  }
  return [];
};

export const WastageTable: React.FC<{ selectedBranch: IBranch }> = (props) => {
  const {
    start,
    end,
    selectedDate,
    selectedDateFrom,
    selectedDateTo,
    handleDateChange,
    handleDateChangeFrom,
    handleDateChangeTo,
  } = useQuerySelectedFromToDates();
  const { status, wastages, isFetching } = useQueryWastages(start, end);
  const [wastage, setWastage] = useState<IWastageFood[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<{
    materialId: string;
    materialName: string;
  }>();
  useEffect(() => {
    if (status === "success") {
      if (!wastages || wastages instanceof FMSError) {
        const message = wastages?.message ?? "Something went wrong!";
        setShowToast({
          open: true,
          message: message,
          type: "error",
        });
      } else {
        setWastage(wastageForBranch(wastages, props.selectedBranch));
      }
    }
  }, [status, wastages, props.selectedBranch]);

  const handleToastClose = () => {
    setShowToast({
      open: false,
      message: showToast.message,
      type: showToast.type,
    });
  };

  const [showToast, setShowToast] = useState<IToastBasicProps>({
    open: false,
    message: "",
    type: "error",
  });

  const columns = React.useMemo(
    () => [
      {
        Header: "Branch Table",
        columns: [
          {
            Header: "Item",
            accessor: "materialName",
            width: 336,
            disableFilters: true,
            sortType: (rowA: any, rowB: any) => {
              return rowA.original.materialName.toUpperCase() >
                rowB.original.materialName.toUpperCase()
                ? 1
                : -1;
            },
            Cell: (props: any) => {
              const wastage = props.row.original as IWastageFood;
              const history = useHistory();
              const queryStr = useLocation().search;
              return (
                <Button
                  style={{
                    display: "block",
                    textAlign: "left",
                    textTransform: "capitalize",
                  }}
                  onClick={() => {
                    if (Auth.getInstance().isAdminOrSupervisor()) {
                      setSelectedMaterial({
                        materialId: wastage.materialId,
                        materialName: wastage.materialName,
                      });
                    } else {
                      history.push({
                        pathname: `/wastage-stats/${wastage.materialId}`,
                        search: queryStr,
                      });
                    }
                  }}
                >
                  {wastage.materialName}
                </Button>
              );
            },
          },
          {
            Header: "Quantity",
            sortType: (rowA: any, rowB: any) => {
              return rowA.original.quantity > rowB.original.quantity ? 1 : -1;
            },
            accessor: (row: IWastageFood) => {
              const unit = displayNameForUnitOfMeasure(
                row.displayQuantity.unit
              );
              return `${row.displayQuantity.quantity} ${unit}`;
            },
            width: 270,
            disableGlobalFilter: true,
            disableFilters: true,
          },
          {
            Header: "Last update",
            sortType: (rowA: any, rowB: any) => {
              if (!rowA.original.lastUpdated) {
                return -1;
              }

              if (!rowB.original.lastUpdated) {
                return 1;
              }

              return rowA.original.lastUpdated > rowB.original.lastUpdated
                ? 1
                : -1;
            },
            accessor: (row: IWastageFood) =>
              moment(row.lastUpdated).format("DD-MM-yyyy"),
            width: 270,
            disableGlobalFilter: true,
            disableFilters: true,
          },
        ],
      },
    ],
    []
  );

  return (
    <>
      <Toast
        open={showToast.open}
        message={showToast.message}
        type={showToast.type}
        onClose={handleToastClose}
      />
      <div
        style={{
          textAlign: "end",
        }}
      >
        <CSVDownload
          filename={`Wastage-${props.selectedBranch.name}-${moment(
            start
          ).format("DD MMM")}-to-${moment(end).format("DD MMM")}`}
          data={getWastageDataForExport(wastage)}
        />
      </div>
      <DataTableWithDates
        isLoading={isFetching}
        columns={columns}
        data={wastage}
        tableClass={styles.table}
        selectedDate={selectedDate!}
        selectedDateFrom={selectedDateFrom}
        selectedDateTo={selectedDateTo}
        handleDateChange={handleDateChange}
        handleDateChangeFrom={handleDateChangeFrom}
        handleDateChangeTo={handleDateChangeTo}
        initialSortBy={"materialName"}
      />
      {selectedMaterial && (
        <ModifyWastageModal
          startDate={start}
          endDate={end}
          materialId={selectedMaterial.materialId}
          selectedBranch={props.selectedBranch}
          materialName={selectedMaterial.materialName}
          onClose={(isWastageEntryUpdated) => {
            setSelectedMaterial(undefined);
            if (isWastageEntryUpdated) {
              invalidateWastagesEntriesCache();
            }
          }}
        />
      )}
    </>
  );
};
